import React from 'react';
import { Box, Button } from '@mui/material';

import { UserRoleSet } from '@paragon/contract-library';
import { sanitizeEmail } from '@paragon/login-tools';
import { enqueueError, enqueueWarning } from '@paragon/notification-tools';

import { createUser, updateUser } from '../actions/userActions';
import { getOrganizationsSorted } from '../selectors/organizations';
import { useAppDispatch, useAppSelector } from './hooks';
import CSV from './tools/CsvParser';

interface UserObject {
  readonly email: string;
  readonly phone: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly organization: string;
  readonly roleSet: UserRoleSet;
  readonly password: string;
  readonly location: string;
  readonly trialEndDate: null;
}

function resetEventTargetValue(event: React.MouseEvent<HTMLInputElement>) {
  (event.target as HTMLInputElement).value = null as unknown as string;
}

export function CsvImport() {
  const dispatch = useAppDispatch();

  const allUsers = useAppSelector((state) => state.userData.users);
  const organizations = useAppSelector((state) => getOrganizationsSorted(state));

  const onSetFile: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const fileList = event.target.files!;
    if (fileList[0] && fileList[0].name.includes('.csv')) {
      const reader = new FileReader();
      reader.readAsText(fileList[0], 'UTF-8');
      reader.onload = (evt) => {
        parseCsv(evt.target!.result as string);
      };
    } else {
      dispatch(enqueueWarning('Only Csv Files!'));
      event.target.value = '';
    }
  };

  function parseCsv(rawText: string) {
    const result = CSV.parse(rawText);
    if (result[0].length < 7) {
      const errorText =
        'Csv requires at least 6 fields: email, phone, firstname, lastname, role_set, organization, password for new users, and optionally location';
      dispatch(enqueueError(errorText));
      throw new Error(errorText);
    }
    const firstNameIndex = result[0].findIndex((o) => o === 'firstname');
    const lastNameIndex = result[0].findIndex((o) => o === 'lastname');
    const phoneNumberIndex = result[0].findIndex((o) => o === 'phone');
    const organizationIndex = result[0].findIndex((o) => o === 'organization');
    const emailIndex = result[0].findIndex((o) => o === 'email');
    const roleSetIndex = result[0].findIndex((o) => o === 'role_set');
    const passwordIndex = result[0].findIndex((o) => o === 'password');
    if (firstNameIndex === -1) {
      dispatch(enqueueError('first name field not found'));
      throw new Error('first name field not found');
    }
    if (lastNameIndex === -1) {
      dispatch(enqueueError('last name field not found'));
      throw new Error('last name field not found');
    }
    if (phoneNumberIndex === -1) {
      dispatch(enqueueError('phone field not found'));
      throw new Error('phone field not found');
    }
    if (organizationIndex === -1) {
      dispatch(enqueueError('organization field not found'));
      throw new Error('organization field not found');
    }
    if (emailIndex === -1) {
      dispatch(enqueueError('email field not found'));
      throw new Error('email field not found');
    }
    if (roleSetIndex === -1) {
      dispatch(enqueueError('role_set field not found'));
      throw new Error('role_set field not found');
    }
    if (passwordIndex === -1) {
      dispatch(enqueueError('password field not found'));
      throw new Error('password field not found');
    }

    const userObjects: UserObject[] = [];
    // build objects
    for (let i = 1; i < result.length; i++) {
      const email = result[i][emailIndex].replace(/'/g, '');
      const organizationName = result[i][organizationIndex].replace(/'/g, '');
      let location: string | undefined;
      // optional location
      if (result[0].findIndex((o) => o === 'location') !== -1) {
        const organization = organizations.find((o) => o.name === organizationName);
        if (organization == null || !organization.locations) {
          if (organization == null) {
            console.warn(`Organization "${organizationName}" does not exist.`);
          } else {
            console.warn(
              `Location specified for user "${email}" does not exist on organization "${organizationName}."`,
            );
          }
          location = 'No Location';
        } else if (
          organization.locations.find((loc) => loc === result[i][result[0].findIndex((o) => o === 'location')]) == null
        ) {
          console.warn(
            `Location specified for user "${email}" does not exist. Defaulting to first organization's location.`,
          );
          location = organization.locations[0].replace(/'/g, '');
        } else {
          location = result[i][result[0].findIndex((o) => o === 'location')].replace(/'/g, '');
        }
      }

      const userObject: UserObject = {
        email,
        phone: result[i][phoneNumberIndex].replace(/'/g, ''),
        firstName: result[i][firstNameIndex].replace(/'/g, ''),
        lastName: result[i][lastNameIndex].replace(/'/g, ''),
        organization: organizationName,
        roleSet: JSON.parse(result[i][roleSetIndex]),
        password: result[i][passwordIndex]?.replace(/'/g, ''),
        location: location!,
        trialEndDate: null,
      };

      // final user object
      userObjects.push(userObject);
    }

    applyCsv(userObjects);
  }

  function userExists(rawEmail: string) {
    const email = sanitizeEmail(rawEmail);
    return allUsers[email];
  }

  function applyCsv(usersObjs: readonly UserObject[]) {
    for (const user of usersObjs) {
      if (userExists(user.email)) {
        dispatch(updateUser(user, user.email));
      } else {
        dispatch(createUser(user));
      }
    }
  }

  return (
    <Box component="label" sx={{ width: '100%' }} htmlFor="import-csv">
      <Box
        component="input"
        sx={{ display: 'none' }}
        id="import-csv"
        multiple
        type="file"
        onChange={onSetFile}
        onClick={resetEventTargetValue}
      />
      <Button fullWidth size="small" variant="outlined" component="span">
        Import CSV
      </Button>
    </Box>
  );
}
